/**
 * Provider-agnostic, privacy-first analytics for the public site.
 *
 * Default provider is Plausible (cookieless, no consent banner required),
 * activated only when VITE_PLAUSIBLE_DOMAIN is set. With no domain configured
 * every call is an inert no-op, so the site ships analytics-ready without
 * committing to a vendor or leaking a tracking script until a domain is wired.
 *
 * Nothing here collects PII: page paths and coarse event names only.
 */

type EventProps = Record<string, string | number | boolean | undefined>;

interface PlausibleFn {
  (event: string, options?: { props?: EventProps; u?: string }): void;
  q?: unknown[];
}

declare global {
  interface Window {
    plausible?: PlausibleFn;
  }
}

const PLAUSIBLE_SRC = "https://plausible.io/js/script.manual.js";
const PLAUSIBLE_SITE_SNIPPET_PREFIX = "https://plausible.io/js/pa-";

function getPlausibleDomain(): string {
  const domain = import.meta.env.VITE_PLAUSIBLE_DOMAIN;
  return typeof domain === "string" ? domain.trim() : "";
}

function isEnabled(): boolean {
  return typeof window !== "undefined" && getPlausibleDomain().length > 0;
}

let initialized = false;

function hasPlausibleWindow(): boolean {
  return typeof window !== "undefined" && typeof window.plausible === "function";
}

function hasSiteSpecificSnippet(): boolean {
  if (typeof document === "undefined") return false;

  return Array.from(document.scripts).some((script) => script.src.startsWith(PLAUSIBLE_SITE_SNIPPET_PREFIX));
}

function shouldUseManualLoader(): boolean {
  return isEnabled() && !hasSiteSpecificSnippet();
}

function canTrack(): boolean {
  return isEnabled() || hasPlausibleWindow() || hasSiteSpecificSnippet();
}

/**
 * Load the analytics script once. Safe to call before the DOM is ready and
 * safe to call more than once. No-ops when no provider domain is configured.
 */
export function initAnalytics(): void {
  if (initialized) {
    return;
  }
  initialized = true;

  if (hasSiteSpecificSnippet()) {
    return;
  }

  if (!shouldUseManualLoader()) {
    return;
  }

  // Queue events until the async script finishes loading.
  window.plausible =
    window.plausible ||
    function queued(...args: unknown[]) {
      (window.plausible!.q = window.plausible!.q || []).push(args);
    };

  const script = document.createElement("script");
  script.defer = true;
  script.src = PLAUSIBLE_SRC;
  script.setAttribute("data-domain", getPlausibleDomain());
  document.head.appendChild(script);
}

/** Record a page view. Call on every client-side route change. */
export function trackPageview(path?: string): void {
  if (!canTrack() || hasSiteSpecificSnippet()) {
    return;
  }
  const url =
    path ?? (typeof window !== "undefined" ? window.location.pathname + window.location.search : undefined);
  try {
    window.plausible?.("pageview", url ? { u: `${window.location.origin}${url}` } : undefined);
  } catch {
    // Analytics must never break the app.
  }
}

/** Record a named conversion/interaction event. */
export function trackEvent(name: string, props?: EventProps): void {
  if (!canTrack()) {
    return;
  }
  try {
    const clean = props
      ? Object.fromEntries(Object.entries(props).filter(([, v]) => v !== undefined && v !== ""))
      : undefined;
    window.plausible?.(name, clean && Object.keys(clean).length > 0 ? { props: clean } : undefined);
  } catch {
    // Analytics must never break the app.
  }
}
